import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setHasViewClicked,
  setInvoiceBody,
  setInvoiceHeader,
  setInvoicePDF,
  setIsGeneratingInvoice,
  setOpenInvoicePreview,
} from '../../redux/slices/feesSlice';
import { RootState } from '../../redux/root';
import { consumePendingInvoiceTab } from './pendingInvoiceTab';

const ViewInvoiceModal = () => {
  const dispatch = useDispatch();
  const { invoicePdf, openInvoicePreview } = useSelector((s: RootState) => s.fees);
  const openedUrlRef = useRef<string | null>(null);

  const clearState = () => {
    // EA-1362: Do NOT clear invoicePdf here. Viewing the invoice from the Send
    // modal previously wiped the generated PDF, so a subsequent "Send" transmitted
    // an empty (~342 byte) attachment. The PDF is reset after a successful send
    // (FeesSendForm) and regenerated when a new invoice is built, so keeping it
    // here is safe and lets View → Send work.
    dispatch(setHasViewClicked(false));
    dispatch(setIsGeneratingInvoice(false));
    dispatch(setOpenInvoicePreview(false));
  };

  useEffect(() => {
    if (!openInvoicePreview || !invoicePdf) return;

    const bytes = atob(invoicePdf);
    let { length } = bytes;
    const out = new Uint8Array(length);
    while (length--) {
      out[length] = bytes.charCodeAt(length);
    }
    const blob = new Blob([out], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    // Revoke the previous URL (if any) before tracking a new one
    if (openedUrlRef.current) {
      URL.revokeObjectURL(openedUrlRef.current);
    }
    openedUrlRef.current = url;

    const pending = consumePendingInvoiceTab();
    if (pending) {
      pending.location.href = url;
    } else {
      window.open(url, '_blank');
    }

    clearState();

    // Release the URL once the browser has had time to load it
    setTimeout(() => {
      if (openedUrlRef.current === url) {
        URL.revokeObjectURL(url);
        openedUrlRef.current = null;
      }
    }, 60_000);
  }, [invoicePdf, openInvoicePreview]);

  useEffect(() => {
    return () => {
      if (openedUrlRef.current) {
        URL.revokeObjectURL(openedUrlRef.current);
        openedUrlRef.current = null;
      }
    };
  }, []);

  return null;
};

export default ViewInvoiceModal;
